import { useEffect, useState } from 'react';
import { useAuthStore } from '../../store/authStore';
import { useNavigate } from 'react-router-dom';
import StatCard from '../../components/StatCard';
import ActivityItem from '../../components/ActivityItem';
import Layout from '../../components/Layout';
import api from '../../api';
import {
  Building2,
  User,
  CheckCircle,
  Settings,
  LayoutDashboard,
  ChevronRight,
  Package,
  Wrench,
  Loader2,
} from 'lucide-react';
import { adminAPI } from '../../api';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';

export default function AdminDashboard() {
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();

  const [stats, setStats] = useState({ tenants: 0, users: 0, system: '✓ Active' });
  const [recent, setRecent] = useState([]);
  const [dbInfo, setDbInfo] = useState(null);
  const [dbError, setDbError] = useState('');
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [maintenanceLoading, setMaintenanceLoading] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [tenantsRes, statusRes, globalSettingsRes] = await Promise.all([
          api.get('/admin/tenants', { params: { page: 1, limit: 1 } }),
          api.get('/admin/system-status'),
          adminAPI.getGlobalSettings().catch(() => ({ settings: {} })),
        ]);

        const tenantsTotal =
          tenantsRes.data?.pagination?.total ?? (tenantsRes.data?.tenants?.length || 0);
        setStats((s) => ({ ...s, tenants: tenantsTotal }));
        setRecent([
          {
            icon: Building2,
            title: 'Tenant list viewed',
            subtitle: new Date().toLocaleString('en-GB'),
            badge: 'info',
            badgeColor: 'gray',
          },
        ]);

        if (statusRes.data?.db) {
          setDbInfo(statusRes.data.db);
        }

        // Load maintenance mode setting
        const maintenanceValue = globalSettingsRes.settings?.portal_maintenance_mode || 'false';
        setMaintenanceMode(maintenanceValue === 'true');
      } catch (e) {
        // fail silently in dev, but record DB message for visibility
        setDbError('Unable to determine current database connection');
      }
    };
    load();
  }, []);

  const handleToggleMaintenance = async () => {
    setMaintenanceLoading(true);
    try {
      const newValue = !maintenanceMode;
      await adminAPI.updateGlobalSetting('portal_maintenance_mode', newValue.toString());
      setMaintenanceMode(newValue);
    } catch (error) {
      console.error('Failed to update maintenance mode:', error);
      alert('Failed to update maintenance mode. Please try again.');
    } finally {
      setMaintenanceLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const renderDbMessage = () => {
    if (!dbInfo) return null;

    const host = dbInfo.host || 'unknown';

    if (dbInfo.isSupabase) {
      return 'Database connection: Supabase (managed PostgreSQL)';
    }

    if (host === 'localhost' || host === '127.0.0.1') {
      return 'Database connection: Local PostgreSQL on localhost (self-hosted, not Supabase)';
    }

    if (host === '192.168.0.144') {
      return 'Database connection: PostgreSQL at 192.168.0.144 (self-hosted, not Supabase)';
    }

    return `Database connection: PostgreSQL at ${host} (self-hosted, not Supabase)`;
  };

  return (
    <Layout>
      {/* Header */}
      <div className="bg-[var(--ds-surface-panel)] border-b border-[var(--ds-border-subtle)] shadow-[var(--ds-shadow-card)]">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <h1 className="text-display text-[var(--ds-text-primary)] font-bold">
            Global Admin Dashboard
          </h1>
          <p className="mt-1 text-meta text-[var(--ds-text-secondary)]">
            System administration and tenant management
          </p>
          {dbInfo && (
            <p className="mt-2 text-xs text-[var(--ds-text-muted)]">
              {renderDbMessage()}
              {!dbInfo.reachable && ' (connection check failed)'}
            </p>
          )}
          {!dbInfo && dbError && (
            <p className="mt-2 text-xs text-[var(--ds-status-danger)]">{dbError}</p>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stat Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-6 mb-6">
          <div className="lg:col-span-2">
            <StatCard title="Total Tenants" value={stats.tenants} icon={Building2} accent="blue" />
          </div>
          <div className="lg:col-span-2">
            <StatCard title="Active Users" value={stats.users} icon={User} accent="purple" />
          </div>
          <div className="lg:col-span-2">
            <StatCard title="System Status" value={stats.system} icon={CheckCircle} accent="green" />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Recent Activity */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle>Recent Activity</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="divide-y divide-[var(--ds-border-divider)]">
                {recent.length === 0 ? (
                  <div className="p-6 text-meta text-[var(--ds-text-muted)]">
                    No recent activity
                  </div>
                ) : (
                  recent.map((a, idx) => (
                    <ActivityItem
                      key={idx}
                      icon={a.icon}
                      title={a.title}
                      subtitle={a.subtitle}
                      badge={a.badge}
                      badgeColor={a.badgeColor}
                    />
                  ))
                )}
              </div>
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Quick Actions</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <button
                    onClick={() => navigate('/admin/tenants')}
                    className="w-full flex items-center justify-between p-4 border border-[var(--ds-border-subtle)] rounded-[var(--ds-radius-md)] hover:bg-[var(--ds-control-field-bg)] transition-colors"
                  >
                    <span className="flex items-center text-[var(--ds-text-primary)]">
                      <Building2 className="mr-3 h-5 w-5" />
                      Manage Tenants
                    </span>
                    <ChevronRight className="h-5 w-5 text-[var(--ds-text-muted)]" />
                  </button>
                  <button
                    onClick={() => navigate('/admin/packages')}
                    className="w-full flex items-center justify-between p-4 border border-[var(--ds-border-subtle)] rounded-[var(--ds-radius-md)] hover:bg-[var(--ds-control-field-bg)] transition-colors"
                  >
                    <span className="flex items-center text-[var(--ds-text-primary)]">
                      <Package className="mr-3 h-5 w-5" />
                      Manage Packages
                    </span>
                    <ChevronRight className="h-5 w-5 text-[var(--ds-text-muted)]" />
                  </button>
                  <button
                    disabled
                    className="w-full flex items-center justify-between p-4 border border-[var(--ds-border-subtle)] rounded-[var(--ds-radius-md)] opacity-60 cursor-not-allowed"
                  >
                    <span className="flex items-center text-[var(--ds-text-muted)]">
                      <Settings className="mr-3 h-5 w-5" />
                      System Settings
                    </span>
                    <ChevronRight className="h-5 w-5 text-[var(--ds-text-muted)]" />
                  </button>
                  <button
                    disabled
                    className="w-full flex items-center justify-between p-4 border border-[var(--ds-border-subtle)] rounded-[var(--ds-radius-md)] opacity-60 cursor-not-allowed"
                  >
                    <span className="flex items-center text-[var(--ds-text-muted)]">
                      <LayoutDashboard className="mr-3 h-5 w-5" />
                      Reports
                    </span>
                    <ChevronRight className="h-5 w-5 text-[var(--ds-text-muted)]" />
                  </button>
                </div>
              </CardContent>
            </Card>

            {/* Portal Maintenance Mode */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Wrench className="mr-2 h-5 w-5" />
                  Portal Settings
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-body font-medium text-[var(--ds-text-primary)]">
                      Maintenance Mode
                    </p>
                    <p className="text-meta text-[var(--ds-text-muted)] mt-1">
                      Enable to show maintenance page for public portal views
                    </p>
                  </div>
                  <button
                    onClick={handleToggleMaintenance}
                    disabled={maintenanceLoading}
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-[var(--ds-accent-focus-ring)] focus:ring-offset-2 ${
                      maintenanceMode
                        ? 'bg-[var(--ds-accent-primary)]'
                        : 'bg-[var(--ds-control-field-border)]'
                    } ${maintenanceLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                    role="switch"
                    aria-checked={maintenanceMode}
                  >
                    <span
                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                        maintenanceMode ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                    {maintenanceLoading && (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <Loader2 className="h-3 w-3 text-white animate-spin" />
                      </div>
                    )}
                  </button>
                </div>
                {maintenanceMode && (
                  <div className="mt-3 p-3 bg-[var(--ds-status-warning-bg)] border border-[var(--ds-status-warning)] rounded-[var(--ds-radius-md)]">
                    <p className="text-xs text-[var(--ds-status-warning)]">
                      ⚠️ Maintenance mode is active. Public portal views will show the maintenance page.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Progress Notice */}
        <Card className="mt-6 bg-[var(--ds-status-success-bg)] border-[var(--ds-status-success)]">
          <CardContent className="p-6">
            <h3 className="text-h2 font-semibold text-[var(--ds-status-success)] mb-2 flex items-center">
              <CheckCircle className="mr-2 h-5 w-5" />
              Phase 2 Ready - Tenant Management
            </h3>
            <p className="text-body text-[var(--ds-status-success)]">
              Phase 1 (Authentication) and Phase 2 (Tenant Management) are now complete!
            </p>
            <p className="text-body text-[var(--ds-status-success)] mt-2">
              You can now create and manage tenants, configure subscriptions, and impersonate tenant
              accounts.
            </p>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}

